import { collection, doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { FB_COLLECTION_PATH } from "../../functions/postActionTypes";
import { MyAnnouncementPost } from "../../types/announcement";

interface SaveAnnouncementInput {
  mode: "create" | "edit";
  profile: {
    UserId: string;
    FirstName?: string;
    LastName?: string;
    Company?: string;
    ProfilePicURL?: string;
    UserLastRoleScheduler?: boolean;
  };
  title: string;
  body: string;
  imageUrl: string;
  isPinned: boolean;
  recipientUids: string[];
  selectedCrewIds: string[];
  myAnnouncementPosts: MyAnnouncementPost[];
  announcementId?: string;
}

export const findPinnedConflict = (
  myAnnouncementPosts: MyAnnouncementPost[],
  currentId: string
): MyAnnouncementPost | undefined => {
  return (myAnnouncementPosts || []).find(
    (a) => a?.isPinned === true && a?.announcementId !== currentId
  );
};

export const setAnnouncementPinned = async ({
  announcementId,
  nextPinned,
  previouslyPinnedId,
}: {
  announcementId: string;
  nextPinned: boolean;
  previouslyPinnedId?: string;
}) => {
  const now = Date.now();

  if (!nextPinned) {
    await updateDoc(doc(db, FB_COLLECTION_PATH.Announcements, announcementId), {
      isPinned: false,
      updatedAt: now,
    });
    return;
  }

  if (previouslyPinnedId) {
    await updateDoc(doc(db, FB_COLLECTION_PATH.Announcements, previouslyPinnedId), {
      isPinned: false,
      updatedAt: now,
    });
  }

  await updateDoc(doc(db, FB_COLLECTION_PATH.Announcements, announcementId), {
    isPinned: true,
    updatedAt: now,
  });
};

export const softDeleteAnnouncement = async (announcementId: string) => {
  await updateDoc(doc(db, FB_COLLECTION_PATH.Announcements, announcementId), {
    status: "deleted",
    updatedAt: Date.now(),
  });
};

export const markAnnouncementAsRead = async ({
  userId,
  announcementId,
}: {
  userId: string;
  announcementId: string;
}) => {
  const feedRef = doc(db, FB_COLLECTION_PATH.Users, userId, FB_COLLECTION_PATH.AnnouncementFeed, announcementId);

  await setDoc(
    feedRef,
    {
      isRead: true,
      readAt: Date.now(),
      updatedAt: Date.now(),
    },
    { merge: true }
  );
};

export const saveAnnouncement = async (input: SaveAnnouncementInput): Promise<string> => {
  const {
    mode,
    profile,
    title,
    body,
    imageUrl,
    isPinned,
    recipientUids,
    selectedCrewIds,
    myAnnouncementPosts,
    announcementId,
  } = input;

  const now = Date.now();
  const currentId = mode === "edit" ? String(announcementId || "") : "";

  if (isPinned) {
    const pinnedConflict = findPinnedConflict(myAnnouncementPosts, currentId);
    if (pinnedConflict?.announcementId) {
      await updateDoc(doc(db, FB_COLLECTION_PATH.Announcements, pinnedConflict.announcementId), {
        isPinned: false,
        updatedAt: now,
      });
    }
  }

  if (mode === "edit") {
    const id = String(announcementId || "");
    if (!id) throw new Error("Missing announcementId for edit mode");

    await updateDoc(doc(db, FB_COLLECTION_PATH.Announcements, id), {
      title: title.trim(),
      body: body.trim(),
      imageUrl: imageUrl.trim() || null,
      isPinned,
      recipientUids: recipientUids.filter(Boolean),
      selectedCrewIds: selectedCrewIds || [],
      updatedAt: now,
    });

    return id;
  }

  const announcementRef = doc(collection(db, FB_COLLECTION_PATH.Announcements));

  await setDoc(announcementRef, {
    announcementId: announcementRef.id,
    authorUid: profile.UserId,
    authorName: `${profile.FirstName || ""} ${profile.LastName || ""}`.trim(),
    authorCompany: profile.Company || "",
    authorProfilePicURL: profile.ProfilePicURL || "../assets/profilePic.png",
    authorRoleAtPostTime: profile?.UserLastRoleScheduler ? "scheduler" : "werker",
    title: title.trim(),
    body: body.trim(),
    imageUrl: imageUrl.trim() || null,
    recipientUids: recipientUids.filter(Boolean),
    selectedCrewIds: selectedCrewIds || [],
    isPinned,
    createdAt: now,
    updatedAt: now,
    status: "active",
  });

  return announcementRef.id;
};
